import "./TestPanelDetailsComponent.scss";
import React, {useCallback, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {IRootReducerState} from "../../../../store/reducers";
import {Field, FieldProps, Form, Formik, FormikHelpers} from "formik";
import FormikInputComponent from "../../../../shared/components/core/formik-input/FormikInputComponent";
import FormikAutoCompleteDropdown
    from "../../../../shared/components/core/formik-auto-complete-dropdown/formikAutoCompleteDropdown";
import ButtonComponent from "../../../../shared/components/core/button/ButtonComponent";
import _ from "lodash";
import {CommonService} from "../../../../shared/services";
import {IAPIResponseType} from "../../../../shared/models/api.model";
import * as Yup from "yup";
import commonService from "../../../../shared/services/common.service";
import ChipComponent from "../../../../shared/components/chip/ChipComponent";
import DrawerDetailsDataKeyValueComponent
    from "../../../../shared/components/drawer-details-data-key-value/DrawerDetailsDataKeyValueComponent";
import FormikSelectDropdownComponent
    from "../../../../shared/components/core/formik-select-dropdown/FormikSelectDropdownComponent";
import useFetch from "../../../../shared/hooks/useFetch";
import {APIConfig} from "../../../../constants";

interface TestPanelDetailsComponentProps {
    testPanelDetails: any;
    onSaved: () => void;
}

const AddTestPanelvailidationSchema = Yup.object({
    name: Yup.string().required('Test Panel Name is required'),
    code: Yup.string().min(4).max(4).required('Test Panel Code is required'),
    sampleType: Yup.string().required('Test Panel Sample Type is required'),
    testIds: Yup.array().min(1).required('Test is required'),
    labIds: Yup.array().nullable(),
    description: Yup.string().nullable(),
})

const AddTestPanelInitialValues = {
    name: '',
    code: '',
    sampleType: '',
    testIds: [],
    labIds: [],
    description: '',
}

const TestPanelDetailsComponent = (props: TestPanelDetailsComponentProps) => {
    const {testPanelDetails, onSaved} = props;

    const [addTestPanelFormInitialValues, setAddTestPanelFormInitialValues] = useState<any>(_.cloneDeep(AddTestPanelInitialValues));
    const [mode, setMode] = useState<'view' | 'edit'>('view');
    const {
        labListLite,
        testListLite: testList,
        sampleTypeWithCollectionDevices
    } = useSelector((state: IRootReducerState) => state.staticData);
    const {currentUser} = useSelector((state: IRootReducerState) => state.account);

    const {
        response: testListLite = [],
        loading: isTestListLiteLoading,
        loadingFailed: isTestListLiteError,
        fetchData: fetchTestList
    } = useFetch<any>();

    const getTestListLite = useCallback((sampleType: string) => {
        fetchTestList({
            url: APIConfig.TEST_LIST_LITE.URL,
            method: APIConfig.TEST_LIST_LITE.METHOD,
            payload: {
                isActive: true,
                sampleType
            }
        })
    }, [fetchTestList])

    useEffect(() => {
        if (testPanelDetails && testList?.length > 0 && labListLite?.length > 0) {
            const testIds = testPanelDetails?.testIds?.map((item: any) => {
                return testList?.find((test: any) => test?.id === item);
            })
            const labIds = testPanelDetails?.labIds?.map((item: any) => {
                return labListLite?.find((lab: any) => lab?.id === item);
            })
            setAddTestPanelFormInitialValues({
                name: testPanelDetails?.name,
                code: testPanelDetails?.code,
                sampleType: testPanelDetails?.sampleType,
                testIds: testIds || [],
                labIds: labIds || [],
                description: testPanelDetails?.description,
            });
        }
    }, [testPanelDetails, testList, labListLite]);
    useEffect(() => {
        testPanelDetails?.sampleType && getTestListLite(testPanelDetails?.sampleType)
    }, [getTestListLite, testPanelDetails?.sampleType]);

    const onTestPanelEdit = useCallback((values: any, {setSubmitting, setErrors, validateForm}: FormikHelpers<any>) => {
        const payload = {...values};
        payload.testIds = payload.testIds.map((item: any) => item.id);
        payload.labIds = payload.labIds.map((item: any) => item.id);
        setSubmitting(true);
        CommonService._test.editTestPanel(testPanelDetails?.id, payload)
            .then((response: IAPIResponseType<any>) => {
                if (response && response?.data) {
                    setAddTestPanelFormInitialValues(_.cloneDeep(AddTestPanelInitialValues));
                }
                setSubmitting(false);
                onSaved();
            })
            .catch((error: any) => {
                setSubmitting(false);
            });
    }, [testPanelDetails, onSaved]);

    return (
        <div className={'test-panel-details-component screen t-form-screen'}>

            {
                mode === 'view' ?
                    <div className="t-form t-form-drawer">
                        <div className="t-form-controls-wrapper">
                            <div className="drawer-sub-header horizontal-vertical-center-align">
                                Test Panel Details
                                {commonService.checkAccess('Test', 'testPanelEdit', currentUser?.userAccessModules) &&
                                    <div className={'edit-button'} onClick={() => setMode('edit')}>
                                        Edit Details
                                    </div>
                                }
                            </div>
                        </div>
                        <div className={'view-details-drawer mrg-top-0'}>
                            <DrawerDetailsDataKeyValueComponent title={'Test Panel Name:'}>
                                {addTestPanelFormInitialValues?.name ? commonService.capitalizeFirstLetterOfEachWord(addTestPanelFormInitialValues?.name) : 'N/A'}
                            </DrawerDetailsDataKeyValueComponent>
                            <DrawerDetailsDataKeyValueComponent title={'Test Panel Code:'}>
                                {addTestPanelFormInitialValues?.code || 'N/A'}
                            </DrawerDetailsDataKeyValueComponent>
                            <DrawerDetailsDataKeyValueComponent title={'Description:'}>
                                {addTestPanelFormInitialValues?.description || 'N/A'}
                            </DrawerDetailsDataKeyValueComponent>
                            <div className={'horizontal-line mrg-bottom-20 mrg-top-20'}></div>
                            <DrawerDetailsDataKeyValueComponent title={'Assigned Lab(s):'}
                            >
                                {addTestPanelFormInitialValues?.labIds?.length > 0 ?
                                    addTestPanelFormInitialValues?.labIds?.map((item: any) => {
                                        return <ChipComponent label={item?.name} className={'mrg-right-10'}/>
                                    })
                                    : 'N/A'}
                            </DrawerDetailsDataKeyValueComponent>
                            <div className={'horizontal-line mrg-bottom-20 mrg-top-20'}></div>
                            <DrawerDetailsDataKeyValueComponent title={'Sample Type:'}>
                                {commonService.capitalizeFirstLetter(addTestPanelFormInitialValues?.sampleType) || 'N/A'}
                            </DrawerDetailsDataKeyValueComponent>
                            <DrawerDetailsDataKeyValueComponent title={'Test(s):'}>
                                {addTestPanelFormInitialValues?.testIds?.length > 0 ?
                                    addTestPanelFormInitialValues?.testIds?.map((item: any) => {
                                        return <ChipComponent label={item?.name} className={'mrg-right-10'}/>
                                    }) : 'N/A'}
                            </DrawerDetailsDataKeyValueComponent>
                        </div>
                    </div>
                    :
                    <div className="t-form t-form-drawer">
                        <Formik
                            initialValues={addTestPanelFormInitialValues}
                            onSubmit={onTestPanelEdit}
                            validationSchema={AddTestPanelvailidationSchema}
                            enableReinitialize={true}
                            validateOnChange={true}
                            validateOnBlur={true}
                        >
                            {({isSubmitting, values, isValid, dirty, setFieldValue, validateForm, errors}) => {
                                // eslint-disable-next-line react-hooks/rules-of-hooks
                                useEffect(() => {
                                    validateForm();
                                }, [values, errors, validateForm]);
                                return (
                                    <Form noValidate={true} className={"panel-add-holder"}>
                                        <div className="t-form-controls-wrapper">
                                            <div className="drawer-sub-header">
                                                Edit Test Panel Details
                                            </div>
                                            <Field name={'name'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Test Panel Name'}
                                                            placeholder={'Enter Test Panel Name'}
                                                            type={"text"}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'code'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Test Panel Code'}
                                                            placeholder={'Enter Test Panel Code'}
                                                            type={"text"}
                                                            maxLength={4}
                                                            required={true}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'sampleType'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikSelectDropdownComponent
                                                            label={'Sample Type'}
                                                            options={sampleTypeWithCollectionDevices}
                                                            placeholder={'Select Sample Type'}
                                                            valueKey={'sampleType'}
                                                            required={true}
                                                            getOptionLabel={(option) => (commonService.capitalizeFirstLetter(option.sampleType)) || ''}
                                                            formikField={field}
                                                            onUpdate={(value: any) => {
                                                                value && getTestListLite(value);
                                                                setFieldValue('testIds', []);
                                                            }
                                                            }
                                                        />
                                                    )}
                                            </Field>
                                            <Field name={'testIds'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            label={'Select Test(s)'}
                                                            placeholder={'Select Test(s)'}
                                                            data={values.sampleType ? (testListLite || []) : []}
                                                            isDataLoading={isTestListLiteLoading}
                                                            disabled={isTestListLiteLoading}
                                                            isDataLoadingFailed={isTestListLiteError}
                                                            multiple={true}
                                                            required={true}
                                                            valueKey={'id'}
                                                            formikField={field}
                                                            getOptionLabel={(option: any) => option?.name}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'labIds'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikAutoCompleteDropdown
                                                            label={'Assign Lab(s)'}
                                                            placeholder={'Select Lab(s)'}
                                                            data={labListLite}
                                                            required={false}
                                                            multiple={true}
                                                            formikField={field}
                                                            valueKey={'id'}
                                                            getOptionLabel={(option: any) => option?.name}
                                                        />
                                                    )
                                                }
                                            </Field>
                                            <Field name={'description'}>
                                                {
                                                    (field: FieldProps) => (
                                                        <FormikInputComponent
                                                            label={'Description (if any)'}
                                                            placeholder={'Enter Description'}
                                                            type={"text"}
                                                            multiline={true}
                                                            rows={4}
                                                            required={false}
                                                            formikField={field}
                                                        />
                                                    )
                                                }
                                            </Field>
                                        </div>
                                        <div className="drawer-actions form-action-section">
                                            <ButtonComponent variant={"contained"}
                                                             fullWidth={true}
                                                             type={"submit"}
                                                             isLoading={isSubmitting}
                                                             disabled={!isValid || !dirty}
                                            >
                                                Save
                                            </ButtonComponent>
                                        </div>
                                    </Form>
                                )
                            }}
                        </Formik>
                    </div>
            }
        </div>
    );

};

export default TestPanelDetailsComponent;
